import type { ThemeStats } from '../../api/adminApi';

const DARK = '#6366f1';
const LIGHT = '#f59e0b';
const UNKNOWN = '#9ca3af';

/** Dark vs light usage — distinct users/day carrying a theme (client_context). */
export function ThemeStatsTab({
  data,
  isLoading,
  rangeLabel,
}: {
  data: ThemeStats | undefined;
  isLoading: boolean;
  rangeLabel: string;
}) {
  if (isLoading) return <div className="loading">Loading…</div>;
  if (!data) return null;

  const { totals, byDay } = data;
  const themed = totals.light + totals.dark;
  const lightPct = themed ? Math.round((totals.light / themed) * 1000) / 10 : 0;
  const dayMax = byDay.reduce((m, d) => Math.max(m, d.light + d.dark + d.unknown), 0) || 1;

  const Stat = ({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) => (
    <div style={{ flex: 1, minWidth: 140, padding: '0.9rem 1rem', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280', fontSize: 13 }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: 'inline-block' }} />
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ color: '#6b7280', fontSize: 13 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: '1rem 0' }}>
      <h3 style={{ marginTop: 0 }}>Dark vs Light — {rangeLabel}</h3>
      <p style={{ color: '#6b7280', marginTop: -8 }}>
        Distinct users per day by the theme they were actually shown (one sample per user per day).
      </p>

      {themed === 0 && totals.unknown === 0 ? (
        <p style={{ color: '#6b7280' }}>No theme data recorded yet for this window.</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, maxWidth: 640 }}>
            <Stat label="Dark" value={totals.dark} sub={`${totals.darkPct}% of themed`} color={DARK} />
            <Stat label="Light" value={totals.light} sub={`${lightPct}% of themed`} color={LIGHT} />
            {totals.unknown > 0 && <Stat label="Unknown" value={totals.unknown} sub="no theme captured" color={UNKNOWN} />}
          </div>

          {/* Overall split bar */}
          <div style={{ maxWidth: 640, marginBottom: 24 }}>
            <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              {totals.dark > 0 && <div style={{ width: `${(totals.dark / (themed + totals.unknown)) * 100}%`, background: DARK }} title={`Dark ${totals.dark}`} />}
              {totals.light > 0 && <div style={{ width: `${(totals.light / (themed + totals.unknown)) * 100}%`, background: LIGHT }} title={`Light ${totals.light}`} />}
              {totals.unknown > 0 && <div style={{ width: `${(totals.unknown / (themed + totals.unknown)) * 100}%`, background: UNKNOWN }} title={`Unknown ${totals.unknown}`} />}
            </div>
          </div>

          {/* Per-day stacked bars */}
          <h4 style={{ margin: '0 0 8px' }}>By day</h4>
          <div style={{ maxWidth: 720 }}>
            {byDay.map((d) => {
              const tot = d.light + d.dark + d.unknown;
              return (
                <div key={d.date} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '3px 0' }}>
                  <span style={{ width: 92, color: '#6b7280', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{d.date}</span>
                  <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 4, height: 16, overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: `${(d.dark / dayMax) * 100}%`, background: DARK }} title={`Dark ${d.dark}`} />
                    <div style={{ width: `${(d.light / dayMax) * 100}%`, background: LIGHT }} title={`Light ${d.light}`} />
                    <div style={{ width: `${(d.unknown / dayMax) * 100}%`, background: UNKNOWN }} title={`Unknown ${d.unknown}`} />
                  </div>
                  <span style={{ width: 36, textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{tot}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
